import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { workspacePopoverTooltipContentClassName } from "@/layouts/WorkspaceLayout/components/workspacePopoverTooltipClassName";
import { cn } from "@/utils";
import type { FileRecord, InboxItem } from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import { MessagesSquare } from "lucide-react";
import { useState, type ReactElement, type ReactNode } from "react";

/**
 * Generic rail hover-preview tooltip — the same chrome as the canvas hero
 * preview, but with an arbitrary preview body. Suppressed while the row is
 * active (you're already viewing it) or a menu is open.
 */
export function RailHoverPreviewTooltip({
  preview,
  active,
  suppress,
  children,
}: {
  preview: ReactNode;
  active?: boolean;
  suppress?: boolean;
  children: ReactElement;
}) {
  const showPreview = !active && !suppress;

  return (
    // grouped: shares the rail's PreviewTooltipProvider warm state — first
    // preview delayed, sliding to neighboring rows is instant (Chrome tabs).
    <Tooltip grouped open={suppress ? false : undefined}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {showPreview && (
        <TooltipContent
          side="right"
          align="start"
          // The trigger is the full-width row: 8px clears the rail scroll
          // container's px-2 to reach the rail's right edge, +4px breathing
          // room — so the preview never overlaps the row's trailing controls
          // (Open pill, kebab).
          sideOffset={12}
          collisionPadding={16}
          avoidCollisions
          hideArrow
          instant
          className={cn("block p-2", workspacePopoverTooltipContentClassName)}
        >
          {preview}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

/** Any available preview thumbnail for a file record (presigned, keyed by size). */
function firstPreviewUrl(record: FileRecord): string | undefined {
  const urls = record.preview_urls;
  if (!urls) return undefined;
  return urls["640"] ?? Object.values(urls)[0];
}

/**
 * Hover preview for a file row — the file's thumbnail when it has one (images,
 * rendered PDFs, etc. carry `preview_urls`), otherwise its colored type icon.
 */
export function RailFilePreviewTooltip({
  record,
  label,
  active,
  suppress,
  children,
}: {
  record: FileRecord;
  label: string;
  active?: boolean;
  suppress?: boolean;
  children: ReactElement;
}) {
  const [imgErr, setImgErr] = useState(false);
  const thumb = imgErr ? undefined : firstPreviewUrl(record);

  const preview = (
    <div className="w-64">
      <div className="truncate px-0.5 pb-2 text-sm font-medium text-n-90">
        {label}
      </div>
      <div className="flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-md border border-n-10 bg-n-05">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="size-full object-cover"
            onError={() => setImgErr(true)}
          />
        ) : (
          <FileRecordTypeIcon
            contentType={record.content_type}
            originType={record.origin_type}
            className="size-8"
          />
        )}
      </div>
    </div>
  );

  return (
    <RailHoverPreviewTooltip
      preview={preview}
      active={active}
      suppress={suppress}
    >
      {children}
    </RailHoverPreviewTooltip>
  );
}

/** The last-message snippet for a chat's hover preview. */
function chatSnippet(item: InboxItem): string | null {
  const raw = item.flow_message?.content;
  if (!raw) return null;
  const cleaned = raw.replace(/\s+/g, " ").trim();
  if (!cleaned) return null;
  return cleaned.length > 240 ? `${cleaned.slice(0, 240)}…` : cleaned;
}

/**
 * Hover preview for a chat row — the last message snippet (matching the
 * detail-page ChatCard), or a placeholder icon when the chat has no messages.
 */
export function RailChatPreviewTooltip({
  item,
  label,
  active,
  suppress,
  children,
}: {
  item: InboxItem;
  label: string;
  active?: boolean;
  suppress?: boolean;
  children: ReactElement;
}) {
  const snippet = chatSnippet(item);

  const preview = (
    <div className="w-64">
      <div className="truncate px-0.5 pb-2 text-sm font-medium text-n-90">
        {label}
      </div>
      <div className="rounded-md border border-n-10 bg-n-05 p-2 text-xs text-n-70">
        {snippet ? (
          <p className="line-clamp-5 whitespace-pre-wrap">{snippet}</p>
        ) : (
          <div className="flex h-16 items-center justify-center">
            <MessagesSquare className="size-6 text-n-30" />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <RailHoverPreviewTooltip
      preview={preview}
      active={active}
      suppress={suppress}
    >
      {children}
    </RailHoverPreviewTooltip>
  );
}
